'use client'
import { useMemo, useState } from 'react'
import { RegularSection, ColorTitle } from '@/components/Layouts'
import { ScrollButton } from '@/components/Inputs'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import { Select as MUISelect, Stack } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import Grid from '@mui/material/Grid'

const inputStyle = {
    fontWeight: 900,
    fontStyle: 'italic',
    fontSize: 'clamp(12px, 1.5vw, 18px)',
    textTransform: 'uppercase',
    fontFamily: 'MonaSansCondensed, sans-serif',
    borderRadius: 2,
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: 'white',
            borderRadius: '15px',
        },
    },
}

interface StepOption {
    value: string
    label: string
    next?: string
}

interface Step {
    id: string
    type: 'select' | 'number' | 'boolean'
    title: string
    options?: StepOption[]
    unit?: string
    min?: number
    max?: number
    step?: number
    labelYes?: string
    labelNo?: string
}

interface PricingRule {
    when: Record<string, any>
    formula: (values: Record<string, any>) => number
}

interface CalcConfig {
    currency: string
    baseFees: {
        mobilization: number
    }
    steps: Step[]
    pricing: {
        rules: PricingRule[]
    }
}

const CALC_CONFIG: CalcConfig = {
    currency: 'CAD',
    baseFees: {
        mobilization: 150,
    },
    steps: [
        {
            id: 'service',
            type: 'select',
            title: 'What service do you need?',
            options: [
                { value: 'cutting', label: 'Cutting (wall/floor)', next: 'cut_depth' },
                { value: 'coring', label: 'Diamond coring', next: 'cor_diameter' },
                { value: 'trenching', label: 'Trenching / chasing', next: 'trench_depth' },
            ],
        },
        {
            id: 'cut_depth',
            type: 'select',
            title: 'How deep?',
            options: [
                { value: 'le10', label: '<= 10"', next: 'cut_area' },
                { value: 'gt10', label: '> 10"', next: 'cut_area' },
            ],
        },
        {
            id: 'cut_area',
            type: 'number',
            title: 'How long in Sq Ft?',
            unit: 'sq ft',
            min: 1,
            max: 100000,
            step: 1,
        },
        {
            id: 'cut_gpr',
            type: 'boolean',
            title: 'Will you require GPR Scanning?',
            labelYes: 'Yes',
            labelNo: 'No',
        },
        {
            id: 'cut_disposal',
            type: 'boolean',
            title: 'Would you like Disposal of concrete debris?',
            labelYes: 'Yes',
            labelNo: 'No',
        },
        {
            id: 'cor_diameter',
            type: 'select',
            title: 'What is your approximate Diameter?',
            options: [
                { value: 'd2_single', label: 'D2" single', next: 'cor_holes' },
                { value: 'd3_single', label: 'D3" single', next: 'cor_holes' },
                { value: 'd4_single', label: 'D4" single', next: 'cor_holes' },
                { value: 'd5_single', label: 'D5" single', next: 'cor_holes' },
                { value: 'd5_open', label: 'D5" open', next: 'cor_holes' },
                { value: 'd6_single', label: 'D6" single', next: 'cor_holes' },
                { value: 'd6_open', label: 'D6" open', next: 'cor_holes' },
                { value: 'd7_single', label: 'D7" single', next: 'cor_holes' },
                { value: 'd7_open', label: 'D7" open', next: 'cor_holes' },
                { value: 'd8_single', label: 'D8" single', next: 'cor_holes' },
                { value: 'd9_single', label: 'D9" single', next: 'cor_holes' },
                { value: 'd10_single', label: 'D10" single', next: 'cor_holes' },
                { value: 'd11_single', label: 'D11" single', next: 'cor_holes' },
                { value: 'd12_5_single', label: 'D12,5" single', next: 'cor_holes' },
            ],
        },
        {
            id: 'cor_holes',
            type: 'number',
            title: 'How many holes?',
            unit: '#',
            min: 1,
            max: 10000,
            step: 1,
        },
        {
            id: 'trench_depth',
            type: 'boolean',
            title: 'Is it 4–8" deep?',
            labelYes: 'Yes',
            labelNo: 'No',
        },
        {
            id: 'trench_pipes',
            type: 'boolean',
            title: 'Are there any kind of pipes under?',
            labelYes: 'Yes',
            labelNo: 'No',
        },
        {
            id: 'trench_gpr',
            type: 'boolean',
            title: 'Will you require GPR Scanning?',
            labelYes: 'Yes',
            labelNo: 'No',
        },
        {
            id: 'trench_report',
            type: 'boolean',
            title: 'Will you require a full Report?',
            labelYes: 'Yes',
            labelNo: 'No',
        },
        {
            id: 'trench_precision',
            type: 'select',
            title: 'Do you want a precise cut or breaking?',
            options: [
                { value: 'cut', label: 'Cut', next: 'trench_length_ft' },
                { value: 'break', label: 'Break', next: 'trench_length_ft' },
            ],
        },
        {
            id: 'trench_length_ft',
            type: 'number',
            title: 'How many ft long approximately?',
            unit: 'ft',
            min: 1,
            max: 100000,
            step: 1,
        },
        {
            id: 'trench_disposal',
            type: 'boolean',
            title: 'Would you like Disposal of concrete debris?',
            labelYes: 'Yes',
            labelNo: 'No',
        },
        {
            id: 'material',
            type: 'select',
            title: 'Base material',
            options: [
                { value: 'concrete', label: 'Concrete' },
                { value: 'asphalt', label: 'Asphalt' },
                { value: 'stone', label: 'Stone / granite' },
            ],
        },
        {
            id: 'thickness',
            type: 'number',
            title: 'Thickness / depth',
            unit: 'cm',
            min: 1,
            max: 200,
            step: 1,
        },
        {
            id: 'length',
            type: 'number',
            title: 'Length / run',
            unit: 'm',
            min: 0.1,
            max: 1000,
            step: 0.1,
        },
        {
            id: 'rebar',
            type: 'boolean',
            title: 'Rebar present?',
            labelYes: 'Yes',
            labelNo: 'No',
        },
        {
            id: 'urgent',
            type: 'boolean',
            title: 'Urgent (within 24h)?',
            labelYes: 'Yes',
            labelNo: 'No',
        },
    ],
    pricing: {
        rules: [
            {
                when: { service: 'cutting' },
                formula: ({ cut_depth, cut_area = 0, cut_gpr, cut_disposal }: Record<string, any>) => {
                    if (!cut_depth) return 0
                    const area = Number(cut_area) || 0
                    const base = cut_depth === 'le10' ? 1200 : 2500
                    const rate = cut_depth === 'le10' ? 30 : 70
                    let sum = (base + area * rate) * 1.15
                    if (cut_gpr === true) sum += 1000
                    else if (cut_gpr === false) sum += 750
                    if (cut_disposal) sum += 100
                    return sum
                },
            },
            {
                when: { service: 'cutting', material: 'asphalt' },
                formula: ({ thickness = 10, length = 1, urgent }: Record<string, any>) => {
                    const basePerMeter = 18
                    const thicknessFactor = Math.max(0, (thickness - 10) / 10)
                    const rate = basePerMeter * (1 + 0.1 * thicknessFactor)
                    let total = rate * length
                    if (urgent) total *= 1.15
                    return total
                },
            },
            {
                when: { service: 'coring' },
                formula: ({ cor_diameter, cor_holes = 0 }: Record<string, any>) => {
                    const rates: Record<string, number> = {
                        d2_single: 60,
                        d3_single: 70,
                        d4_single: 80,
                        d5_single: 90,
                        d5_open: 80,
                        d6_single: 100,
                        d6_open: 85,
                        d7_single: 125,
                        d7_open: 100,
                        d8_single: 150,
                        d9_single: 200,
                        d10_single: 250,
                        d11_single: 300,
                        d12_5_single: 350,
                    }
                    const rate = rates[cor_diameter] || 0
                    const qty = Number(cor_holes) || 0
                    return rate * qty
                },
            },
            {
                when: { service: 'trenching' },
                formula: ({
                    trench_depth,
                    trench_pipes,
                    trench_report,
                    trench_precision,
                    trench_length_ft,
                    trench_disposal,
                }: Record<string, any>) => {
                    if (trench_depth === false) return 0
                    if (trench_depth !== true) return 0

                    let sum = 1750
                    if (trench_pipes === true) sum += 1500

                    if (trench_report === true) sum += 1000
                    else if (trench_report === false) sum += 750

                    const len = Number(trench_length_ft) || 0
                    if (trench_precision === 'cut') sum += len * 30
                    else if (trench_precision === 'break') sum += len * 15

                    if (trench_disposal) sum += 100

                    return sum
                },
            },
        ],
    },
}

interface SelectStepProps {
    step: Step
    value: any
    onSelect: (value: any) => void
    onAdvance?: (value: any) => void
}

const SelectStep = ({ step, value, onSelect, onAdvance }: SelectStepProps) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', alignItems: 'center' }}>
            {step.options?.map((option) => (
                <Button
                    key={option.value}
                    variant={value === option.value ? 'contained' : 'outlined'}
                    sx={{ width: 320, height: 64 }}
                    onClick={() => {
                        onSelect(option.value)
                        onAdvance?.(option.value)
                    }}
                >
                    {option.label}
                </Button>
            ))}
        </Box>
    )
}

interface NumberStepProps {
    step: Step
    value: number | string
    onChange: (value: number | string) => void
    onAdvance?: (value: number | string) => void
}

const NumberStep = ({ step, value, onChange, onAdvance }: NumberStepProps) => {
    return (
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
            <TextField
                type='number'
                focused
                label={step.unit ? `${step.title} (${step.unit})` : step.title}
                inputProps={{ min: step.min, max: step.max, step: step.step || 1 }}
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
                sx={{ width: 360, ...inputStyle }}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                        const target = e.target as HTMLInputElement
                        const v = target.value === '' ? '' : Number(target.value)
                        if (v !== '' && !Number.isNaN(v)) onAdvance?.(v)
                    }
                }}
            />
        </Box>
    )
}

interface BooleanStepProps {
    step: Step
    value: boolean | undefined
    onSelect: (value: boolean) => void
    onAdvance?: (value: boolean) => void
}

const BooleanStep = ({ step, value, onSelect, onAdvance }: BooleanStepProps) => {
    return (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
                variant={value === true ? 'contained' : 'outlined'}
                onClick={() => {
                    onSelect(true)
                    onAdvance?.(true)
                }}
                sx={{ width: 160, height: 56 }}
            >
                {step.labelYes || 'Yes'}
            </Button>
            <Button
                variant={value === false ? 'contained' : 'outlined'}
                onClick={() => {
                    onSelect(false)
                    onAdvance?.(false)
                }}
                sx={{ width: 160, height: 56 }}
            >
                {step.labelNo || 'No'}
            </Button>
        </Box>
    )
}

interface SelectDropdownStepProps {
    step: Step
    value: any
    onSelect: (value: any) => void
    onAdvance?: (value: any) => void
}

const SelectDropdownStep = ({ step, value, onSelect, onAdvance }: SelectDropdownStepProps) => {
    return (
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
            <FormControl sx={{ width: 360 }}>
                <InputLabel id={`${step.id}-label`}>{step.title}</InputLabel>
                <MUISelect
                    labelId={`${step.id}-label`}
                    id={`${step.id}-select`}
                    value={value ?? ''}
                    label={step.title}
                    onChange={(e) => {
                        onSelect(e.target.value)
                        onAdvance?.(e.target.value)
                    }}
                >
                    {step.options?.map((option) => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </MUISelect>
            </FormControl>
        </Box>
    )
}

function matchRule(when: Record<string, any>, values: Record<string, any>): boolean {
    return Object.entries(when).every(([k, v]) => values[k] === v)
}

function findPricingRule(values: Record<string, any>): PricingRule | undefined {
    return CALC_CONFIG.pricing.rules.find((r) => matchRule(r.when, values))
}

function formatCurrency(amount: number, currency: string = CALC_CONFIG.currency): string {
    try {
        return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(amount)
    } catch {
        return `${amount.toFixed(2)} ${currency}`
    }
}

function getNextIdForSelect(step: Step | undefined, value: any): string | undefined {
    if (!step || step.type !== 'select') return undefined
    const opt = step.options?.find((o) => o.value === value)
    return opt?.next
}

function getStepById(id: string): Step | undefined {
    return CALC_CONFIG.steps.find((s) => s.id === id)
}

function getOptionLabel(step: Step | undefined, value: any): string {
    const opt = step?.options?.find((o) => o.value === value)
    return opt?.label ?? String(value)
}

function formatValueForSummary(step: Step | undefined, value: any): string {
    if (!step) return typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)
    if (step.type === 'boolean') return value ? 'Yes' : 'No'
    if (step.type === 'select') return getOptionLabel(step, value)
    if (step.type === 'number') {
        const unit = step.unit ? ` ${step.unit}` : ''
        return `${value}${unit}`
    }
    return String(value)
}

function labelForSummary(step: Step | undefined): string {
    return step?.title || ''
}

interface StepRendererProps {
    step: Step
    value: any
    onChange: (value: any) => void
    onAdvance?: (value: any) => void
}

const StepRenderer = ({ step, value, onChange, onAdvance }: StepRendererProps) => {
    if (step.id === 'cor_diameter')
        return (
            <SelectDropdownStep
                step={step}
                value={value}
                onSelect={onChange}
                onAdvance={onAdvance}
            />
        )
    if (step.type === 'select')
        return (
            <SelectStep
                step={step}
                value={value}
                onSelect={onChange}
                onAdvance={onAdvance}
            />
        )
    if (step.type === 'number')
        return (
            <NumberStep
                step={step}
                value={value}
                onChange={onChange}
                onAdvance={onAdvance}
            />
        )
    if (step.type === 'boolean')
        return (
            <BooleanStep
                step={step}
                value={value}
                onSelect={onChange}
                onAdvance={onAdvance}
            />
        )
    return null
}

interface SummaryProps {
    values: Record<string, any>
    price: number
    currency: string
}

const Summary = ({ values, price, currency }: SummaryProps) => {
    return (
        <Box sx={{ mt: 3, p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography
                variant='body1'
                sx={{ mb: 1, fontSize: '1.6rem' }}
            >
                Summary:
            </Typography>
            {Object.entries(values).map(([k, v]) => {
                const step = getStepById(k)
                if (!step) return null
                const label = labelForSummary(step)
                const display = formatValueForSummary(step, v)
                return (
                    <Typography
                        key={k}
                        variant='body2'
                    >
                        {label}: <strong>{display}</strong>
                    </Typography>
                )
            })}
            <Divider sx={{ my: 1.5 }} />
            <Typography variant='body1' sx={{fontSize: '1.6rem' }}>Estimate: {formatCurrency(price, currency)}</Typography>
            <Typography
                variant='caption'
                color='text.secondary'
            >
                * This is an approximate estimate. Final pricing may vary. Use this as a guide to better understand
                potential costs.
            </Typography>
        </Box>
    )
}

interface CalculatorProps {
    blockName?:string
}

const Calculator = ({ blockName: _blockName }: CalculatorProps) => {
    const [activeStep, setActiveStep] = useState(0)
    const [values, setValues] = useState<Record<string, any>>({})
    const [_history, setHistory] = useState<string[]>([])
    const [serviceUnavailable, setServiceUnavailable] = useState(false)

    const stepIndexById = useMemo(() => {
        const map: Record<string, number> = Object.create(null)
        CALC_CONFIG.steps.forEach((s, idx) => {
            map[s.id] = idx
        })
        return map
    }, [])

    const step = CALC_CONFIG.steps[activeStep]

    const canProceed = useMemo(() => {
        const v = values[step?.id]
        if (step?.type === 'number') return v !== '' && typeof v === 'number' && !Number.isNaN(v)
        return v !== undefined
    }, [step, values])

    const handleChange = (val: any) => {
        if (step) {
            setValues((prev) => ({ ...prev, [step.id]: val }))
        }
    }

    const handleNext = (overrideValue?: any) => {
        if (!step) return
        const currentValue = overrideValue !== undefined ? overrideValue : values[step.id]
        setHistory((prev) => [...prev, step.id])

        if (step.id === 'trench_depth' && values.service === 'trenching' && currentValue === false) {
            setActiveStep(CALC_CONFIG.steps.length)
            setServiceUnavailable(true)
            return
        }
        if (step.id === 'trench_disposal' && values.service === 'trenching') {
            setActiveStep(CALC_CONFIG.steps.length)
            return
        }
        if (step.id === 'cut_disposal' && values.service === 'cutting') {
            setActiveStep(CALC_CONFIG.steps.length)
            return
        }
        if (step.id === 'cor_holes' && values.service === 'coring') {
            setActiveStep(CALC_CONFIG.steps.length)
            return
        }
        let targetIndex = activeStep + 1
        if (step.type === 'select') {
            const chosen = overrideValue !== undefined ? overrideValue : values[step.id]
            const nextId = getNextIdForSelect(step, chosen)
            if (nextId && stepIndexById[nextId] != null) {
                targetIndex = stepIndexById[nextId]
            }
        }
        if (targetIndex < CALC_CONFIG.steps.length) setActiveStep(targetIndex)
    }

    const handleBack = () => {
        setHistory((prev) => {
            if (!prev.length) return prev
            const copy = prev.slice(0, -1)
            const lastId = prev[prev.length - 1]
            const idx = stepIndexById[lastId]
            if (idx != null) setActiveStep(idx)
            return copy
        })
    }

    const subtotal = useMemo(() => {
        const rule = findPricingRule(values)
        if (!rule) return 0
        const work = Number(rule.formula(values)) || 0
        const fees = work > 0 ? CALC_CONFIG.baseFees.mobilization || 0 : 0
        return work + fees
    }, [values])

    const isFinal = activeStep === CALC_CONFIG.steps.length

    return (
        <RegularSection aria-label='Calculator' >
            <Box
                id='quota-calculator'
                sx={{ scrollMarginTop: { xs: '80px', md: '120px' } }}
            />
            <ColorTitle
                title='Service Cost Calculator'
                colorFrom={0}
                textAlign='center'
                width='100%'
            />
            <Grid
                container
                spacing={3}
                sx={{ width: '100%', minHeight: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
                <Grid size={{ xs: 12, md: 6 }}>
                    {!isFinal && step && (
                        <>
                            <Typography
                                variant='body1'
                                sx={{ textAlign: 'center', mb: 2, fontSize: '1.8rem' }}
                            >
                                {step.title}
                            </Typography>

                            <StepRenderer
                                step={step}
                                value={values[step.id]}
                                onChange={handleChange}
                                onAdvance={(val) => handleNext(val)}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 2,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    mt: 3,
                                }}
                            >
                                <Button
                                    onClick={handleBack}
                                    disabled={activeStep === 0}
                                    startIcon={<ChevronLeftIcon />}
                                    size='large'
                                >
                                    Back
                                </Button>
                                <Button
                                    startIcon={<ChevronRightIcon />}
                                    onClick={() => handleNext()}
                                    disabled={!canProceed || isFinal}
                                    size='large'
                                >
                                    Next
                                </Button>
                            </Box>
                        </>
                    )}
                    {isFinal && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2 }}>
                            <Typography
                                variant='h4'
                                textAlign='center'
                            >
                                Calculation completed.
                            </Typography>
                            <Stack
                                direction={'row'}
                                spacing={2}
                                sx={{ width: '100%', justifyContent: 'center' }}
                            >
                                <Button
                                    variant='outlined'
                                    onClick={() => {
                                        setValues({})
                                        setHistory([])
                                        setActiveStep(0)
                                        setServiceUnavailable(false)
                                    }}
                                >
                                    {'Start over'}
                                </Button>

                                <ScrollButton
                                    variant='contained'
                                    color='primary'
                                    size='large'
                                    sx={{ width: 'fit-content' }}
                                    gotoId='contact-form'
                                >
                                    {'Contact us now'}
                                </ScrollButton>
                            </Stack>
                        </Box>
                    )}
                </Grid>
                <Grid
                    size={{ xs: 12, md: 6 }}
                    sx={{ height: '100%', justifyContent: 'start', alignItems: 'start' }}
                >
                    {serviceUnavailable ? (
                        <Typography
                            variant='h4'
                            color='error'
                            sx={{ mt: 3, p: 2 }}
                        > {`Sorry, we don't offer such services!`}
                        </Typography>
                    ) : (
                        <Summary
                            values={values}
                            price={subtotal}
                            currency={CALC_CONFIG.currency}
                        />
                    )}
                </Grid>
            </Grid>
        </RegularSection>
    )
}

export default Calculator

